import { useEffect, useCallback } from 'react';
import { io } from "socket.io-client";
import {
  hydrateSessionFromSource,
  patchSessionState,
  mergeGameStateIntoMap,
} from '../utils/gameStateManager';

/** Survives Webpack Fast Refresh so HMR does not open a new Socket.IO session each save. */
const SOCKET_SINGLETON_KEY = '__gridGlorySocketIoClient';

function createSocket() {
  return io(process.env.REACT_APP_SERVER_URL, {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    secure: true,
    rejectUnauthorized: false,
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  });
}

function getSharedSocket() {
  if (typeof window !== 'undefined') {
    if (!window[SOCKET_SINGLETON_KEY]) {
      window[SOCKET_SINGLETON_KEY] = createSocket();
    }
    return window[SOCKET_SINGLETON_KEY];
  }
  return createSocket();
}

const socket = getSharedSocket();

// Check if in presenter mode (skip localStorage operations)
const isPresenterMode = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('isPresenter') === 'true';
  } catch (e) {
    return false;
  }
};

// Helper function to get stored room info
const getStoredRoomInfo = () => {
  // Skip localStorage in presenter mode
  if (isPresenterMode()) {
    return { roomId: null, userName: null };
  }
  try {
    const roomId = localStorage.getItem('currentRoomId');
    const userName = localStorage.getItem('currentUserName');
    return { roomId, userName };
  } catch (e) {
    console.error('Error reading room info from localStorage:', e);
    return { roomId: null, userName: null };
  }
};

// Helper function to store room info
const storeRoomInfo = (roomId, userName) => {
  // Skip localStorage in presenter mode
  if (isPresenterMode()) {
    return;
  }
  try {
    if (roomId && userName) {
      localStorage.setItem('currentRoomId', roomId);
      localStorage.setItem('currentUserName', userName);
    }
    // Always store the last used username separately for autofill
    if (userName) {
      localStorage.setItem('lastUserName', userName);
    }
  } catch (e) {
    console.error('Error storing room info to localStorage:', e);
  }
};

// Helper function to clear stored room info (but keep last username)
const clearStoredRoomInfo = () => {
  // Skip localStorage in presenter mode
  if (isPresenterMode()) {
    return;
  }
  try {
    localStorage.removeItem('currentRoomId');
    localStorage.removeItem('currentUserName');
    // Don't remove 'lastUserName' - keep it for autofill in future rooms
  } catch (e) {
    console.error('Error clearing room info from localStorage:', e);
  }
};

// Helper function to get last used username for autofill
const getLastUserName = () => {
  // Skip localStorage in presenter mode
  if (isPresenterMode()) {
    return '';
  }
  try {
    return localStorage.getItem('lastUserName') || '';
  } catch (e) {
    console.error('Error reading last username from localStorage:', e);
    return '';
  }
};

/**
 * @param {object} params
 * @param {object} params.grid - dispatchGameState, setSelectedBattlemapId (joinRoom)
 * @param {object} params.tokens - setInitiativeOrder (roomJoined / roomDeleted)
 * @param {object} params.room - lobby setters
 * @param {object} params.maps - setMaps, setCurrentMapId, setViewingMapId
 * @param {() => string|null|undefined} params.getViewingMapId - which map the UI is showing (for gameStatePatched)
 * @param {() => void} [params.purgeLocalSessionAfterRoomEnd] - clear maps + gridAndGloryState for players only (caller no-ops for DM/creator)
 */
export const useSocket = ({
  grid,
  tokens,
  room,
  maps,
  navigate,
  isActivatingFromLocalRef,
  getViewingMapId = () => null,
  purgeLocalSessionAfterRoomEnd,
  onRoomDeleted,
}) => {
  const { setSelectedBattlemapId, dispatchGameState } = grid;
  const { setInitiativeOrder } = tokens;
  const {
    setConnectedUsers,
    setPermissions,
    setRoomCreator,
    setRoomId,
    setUserName,
  } = room;
  const { setMaps, setCurrentMapId, setViewingMapId } = maps;
  const createRoom = useCallback(async (initialData) => {
    try {
      console.log('EMIT: createRoom', initialData);
      const response = await new Promise((resolve, reject) => {
        socket.emit('createRoom', initialData, ({ roomId, creator }) => {
          console.log('RESPONSE: createRoom', { roomId, creator });
          if (roomId) {
            // Store room info for reconnection
            storeRoomInfo(roomId, initialData.userName);
            resolve({ roomId, creator });
          } else {
            reject(new Error('Failed to create room'));
          }
        });
      });
      return response;
    } catch (error) {
      console.error('Error creating room:', error);
      throw error;
    }
  }, []);

  const joinRoom = useCallback(async (roomIdToJoin, userName) => {
    try {
      console.log('EMIT: joinRoom', { roomId: roomIdToJoin, userName });
      const response = await new Promise((resolve, reject) => {
        socket.emit('joinRoom', { roomId: roomIdToJoin, userName }, (response) => {
          console.log('RESPONSE: joinRoom', {
            success: response.success,
            battlemapId: response.battlemapId,
            currentMapId: response.currentMapId,
            gridWidth: response.gridWidth,
            gridHeight: response.gridHeight,
            mapsCount: response.maps?.length,
            maps: response.maps?.map(m => ({ id: m.id, name: m.name, battlemapId: m.battlemapId }))
          });
          if (response.success) {
            // Store room info for reconnection
            storeRoomInfo(roomIdToJoin, userName);
            if (response.maps) {
              setMaps(response.maps);
            }
            if (response.currentMapId) {
              setCurrentMapId(response.currentMapId);
              // Also set viewingMapId to match for players
              if (setViewingMapId) {
                setViewingMapId(response.currentMapId);
              }
            }
            // Set the battlemap from the current map
            if (setSelectedBattlemapId && response.battlemapId !== undefined) {
              console.log('[joinRoom] Setting battlemapId:', response.battlemapId);
              setSelectedBattlemapId(response.battlemapId || 'none');
            }
            resolve(response);
          } else {
            reject(new Error(response.message));
          }
        });
      });
      return response;
    } catch (error) {
      throw error;
    }
  }, [setMaps, setCurrentMapId, setViewingMapId, setSelectedBattlemapId]);

  const addUser = useCallback((userName, isPresenter = false) => {
    console.log('EMIT: addUser', { userName, isPresenter });
    socket.emit('addUser', { userName, isPresenter });
  }, []);

  const patchGameState = useCallback((roomId, mapId, patch, extras = {}) => {
    const { permissions } = extras;
    const hasPatch = patch && typeof patch === 'object' && Object.keys(patch).length > 0;
    const hasPermissions = permissions && typeof permissions === 'object';
    if (!roomId || !mapId || (!hasPatch && !hasPermissions)) return;
    console.log('EMIT: gameStatePatched', {
      roomId,
      mapId,
      patchKeys: hasPatch ? Object.keys(patch) : [],
      hasPermissions,
    });
    socket.emit('gameStatePatched', {
      roomId,
      mapId,
      ...(hasPatch ? { patch } : {}),
      ...(hasPermissions ? { permissions } : {}),
    });
  }, []);

  const leaveRoom = useCallback((roomId, userName) => {
    console.log('ACTION: leaveRoom - removing listeners and leaving room', { roomId, userName });
    
    // Clear stored room info
    clearStoredRoomInfo();
    
    // Notify server that user is leaving the room
    if (roomId && userName) {
      console.log('EMIT: leaveRoom', { roomId, userName });
      socket.emit('leaveRoom', { roomId, userName });
    }
    
    // Remove all room-specific socket listeners
    socket.off('gameStatePatched');
    socket.off('updateConnectedUsers');
    socket.off('roomJoined');
    socket.off('roomDeleted');
  }, []);

  // Map management functions
  const createMap = useCallback(async (roomId, mapData) => {
    try {
      console.log('EMIT: createMap', { roomId, mapData });
      const response = await new Promise((resolve, reject) => {
        socket.emit('createMap', { roomId, mapData }, (response) => {
          console.log('RESPONSE: createMap', response);
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.message));
          }
        });
      });
      return response;
    } catch (error) {
      console.error('Error creating map:', error);
      throw error;
    }
  }, []);

  const duplicateMap = useCallback(async (roomId, mapId) => {
    try {
      console.log('EMIT: duplicateMap', { roomId, mapId });
      const response = await new Promise((resolve, reject) => {
        socket.emit('duplicateMap', { roomId, mapId }, (response) => {
          console.log('RESPONSE: duplicateMap', response);
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.message));
          }
        });
      });
      return response;
    } catch (error) {
      console.error('Error duplicating map:', error);
      throw error;
    }
  }, []);

  const switchMap = useCallback(async (roomId, mapId) => {
    try {
      console.log('[Socket] - Sending switchMap event', { roomId, mapId });
      const response = await new Promise((resolve, reject) => {
        socket.emit('switchMap', { roomId, mapId }, (response) => {
          console.log('[Socket] - Response from switchMap event:', response);
          if (response && response.success) {
            resolve(response);
          } else {
            console.error('[Socket] - Error in switchMap response:', response);
            reject(new Error(response ? response.message : 'Failed to switch map'));
          }
        });
      });
      return response;
    } catch (error) {
      console.error('[Socket] - Error switching map:', error);
      throw error;
    }
  }, []);

  const renameMap = useCallback(async (roomId, mapId, newName) => {
    try {
      console.log('EMIT: renameMap', { roomId, mapId, newName });
      const response = await new Promise((resolve, reject) => {
        socket.emit('renameMap', { roomId, mapId, newName }, (response) => {
          console.log('RESPONSE: renameMap', response);
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.message));
          }
        });
      });
      return response;
    } catch (error) {
      console.error('Error renaming map:', error);
      throw error;
    }
  }, []);

  const deleteMap = useCallback(async (roomId, mapId) => {
    try {
      console.log('EMIT: deleteMap', { roomId, mapId });
      const response = await new Promise((resolve, reject) => {
        socket.emit('deleteMap', { roomId, mapId }, (response) => {
          console.log('RESPONSE: deleteMap', response);
          if (response.success) {
            resolve(response);
          } else {
            reject(new Error(response.message));
          }
        });
      });
      return response;
    } catch (error) {
      console.error('Error deleting map:', error);
      throw error;
    }
  }, []);

  useEffect(() => {
    // Socket event listeners
    console.log('SETUP: Adding socket event listeners');
    
    // Handle socket reconnection
    socket.on('connect', () => {
      console.log('Socket connected');
      
      // Check if we have stored room info for reconnection
      const { roomId, userName } = getStoredRoomInfo();
      if (roomId && userName) {
        console.log('Attempting to reconnect to room:', roomId);
        socket.emit('reconnect', { roomId, userName });
      }
    });

    socket.on('reconnected', (response) => {
      console.log('ON: reconnected', response);
      if (response.success) {
        console.log('Successfully reconnected to room');
        // Get the stored userName to restore it
        const { userName } = getStoredRoomInfo();
        
        // Update all state from server
        setRoomId(response.roomId || getStoredRoomInfo().roomId);
        setUserName(userName); // Restore the userName so isCreator check works
        hydrateSessionFromSource(dispatchGameState, response);
        setPermissions(response.permissions);
        setRoomCreator(response.creator);
        if (response.maps) {
          setMaps(response.maps);
        }
        if (response.currentMapId) {
          setCurrentMapId(response.currentMapId);
          // Also set viewingMapId to match for players
          if (setViewingMapId) {
            setViewingMapId(response.currentMapId);
          }
        }
      } else {
        console.error('Reconnection failed:', response.message);
        // Clear stored room info if reconnection failed
        clearStoredRoomInfo();
        purgeLocalSessionAfterRoomEnd?.();
        setRoomId(null);
        alert('Could not reconnect to room: ' + response.message);
        navigate('/');
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
    });

    socket.on('gameStatePatched', ({ mapId, patch, permissions }) => {
      console.log('ON: gameStatePatched', {
        mapId,
        patchKeys: patch ? Object.keys(patch) : [],
        hasPermissions: !!permissions,
      });
      if (!mapId) return;
      if (patch && Object.keys(patch).length > 0) {
        setMaps((prevMaps) =>
          prevMaps.map((m) =>
            m.id === mapId ? mergeGameStateIntoMap(m, patch) : m
          )
        );
        const viewingId = getViewingMapId();
        if (viewingId === mapId) {
          patchSessionState(dispatchGameState, patch);
        }
      }
      if (permissions) {
        setPermissions(permissions);
      }
    });

    socket.on('roomDeleted', (message) => {
      console.log('ON: roomDeleted', message);
      purgeLocalSessionAfterRoomEnd?.();
      // Use callback to show dialog instead of alert
      if (onRoomDeleted) {
        onRoomDeleted(message);
      }
      leaveRoom();
      setRoomId(null);
      setRoomCreator(''); // Clear room creator
      setConnectedUsers([]);
      setInitiativeOrder([]);
      setUserName(''); // Clear username to prevent name modal from showing
    });

    socket.on('updateConnectedUsers', (users, creator) => {
      console.log('ON: updateConnectedUsers', { users, creator });
      setConnectedUsers(users.map(user => {
        // Handle both old format (string) and new format ({ name, isPresenter })
        const userName = typeof user === 'string' ? user : user.name;
        const isPresenter = typeof user === 'object' ? user.isPresenter : false;
        return {
          name: userName,
          isCreator: userName === creator,
          isPresenter: isPresenter
        };
      }));
      if (creator) {
        setRoomCreator(creator);
      }
    });

    socket.on('roomJoined', ({ 
      roomId, 
      gridWidth, 
      gridHeight, 
      coins, 
      colors, 
      gridWalls,
      permissions, 
      creator,
      initiativeOrder,
      maps,
      currentMapId
    }) => {
      console.log('ON: roomJoined', { 
        roomId, gridWidth, gridHeight, coins, colors, 
        permissions, creator, initiativeOrder, maps, currentMapId
      });
      setRoomId(roomId);
      hydrateSessionFromSource(dispatchGameState, {
        gridWidth,
        gridHeight,
        coins,
        gridColors: colors,
        gridWalls,
      });
      setPermissions(permissions);
      setRoomCreator(creator);
      if (initiativeOrder) setInitiativeOrder(initiativeOrder);
      if (maps) {
        setMaps(maps);
      }
      if (currentMapId) {
        setCurrentMapId(currentMapId);
        // Also set viewingMapId to match currentMapId for players
        // This ensures players are always viewing the active map
        if (setViewingMapId) {
          setViewingMapId(currentMapId);
        }
      }
    });

    // Add map-related event listeners
    socket.on('mapListUpdated', (newMaps) => {
      console.log('ON: mapListUpdated', newMaps);
      // Merge id/name updates with existing map data to preserve local state
      // Server only sends {id, name} - we keep existing battlemapId, gridColors, etc.
      setMaps(prevMaps => {
        const existingMapsById = {};
        prevMaps.forEach(map => {
          existingMapsById[map.id] = map;
        });
        
        // Build new list: preserve existing data, just update id/name, add new maps
        const mergedMaps = newMaps.map(newMap => {
          const existingMap = existingMapsById[newMap.id];
          if (existingMap) {
            // Existing map - preserve all data, just update name if changed
            return { ...existingMap, name: newMap.name };
          }
          // New map from server - use minimal data (player will get full data on map switch)
          return { id: newMap.id, name: newMap.name };
        });
        
        return mergedMaps;
      });
    });
    
    socket.on('mapSwitched', ({ mapId, gridWidth, gridHeight, gridColors, gridWalls, gridObjects, gridObjectInstances, coins, initiativeOrder, battlemapId }) => {
      console.log('[Socket] - Received mapSwitched event:', {
        mapId,
        gridWidth,
        gridHeight,
        battlemapId,
        coinsCount: coins?.length,
        hasGridColors: !!gridColors,
        hasGridObjects: !!gridObjects,
        hasGridObjectInstances: !!gridObjectInstances,
        hasInitiativeOrder: !!initiativeOrder
      });
      
      // If we're activating from local state, don't override our state with server data
      if (isActivatingFromLocalRef && isActivatingFromLocalRef.current) {
        console.log('[Socket] - Using local state instead of server data - ignoring server response');
        // Still update currentMapId and viewingMapId
        setCurrentMapId(mapId);
        if (setViewingMapId) {
          setViewingMapId(mapId);
        }
        // Reset the flag after a delay to ensure no race conditions with other events
        setTimeout(() => {
          isActivatingFromLocalRef.current = false;
          console.log('[Socket] - Reset activation flag, will use server data for future map switches');
        }, 200);
        return;
      }
      
      console.log('[Socket] - Updating state with server data');
      
      // IMPORTANT: Update the maps array FIRST with the new data
      // This ensures the Map View Effect loads the correct data when viewingMapId changes
      setMaps(prevMaps => {
        const updatedMaps = prevMaps.map(map => {
          if (map.id === mapId) {
            return mergeGameStateIntoMap(map, {
              gridWidth: gridWidth || map.gridWidth,
              gridHeight: gridHeight || map.gridHeight,
              gridColors: gridColors || map.gridColors,
              gridWalls: gridWalls || map.gridWalls || {},
              gridObjects: gridObjects || map.gridObjects || {},
              gridObjectInstances: gridObjectInstances || map.gridObjectInstances || {},
              coins: coins || map.coins,
              initiativeOrder: initiativeOrder || map.initiativeOrder,
              battlemapId: battlemapId || 'none'
            });
          }
          return map;
        });
        console.log('[Socket] - Updated maps array with battlemapId:', battlemapId);
        return updatedMaps;
      });
      
      // Now update currentMapId and viewingMapId
      setCurrentMapId(mapId);
      if (setViewingMapId) {
        setViewingMapId(mapId);
      }
      
      // Also directly update the state to be safe
      setTimeout(() => {
        hydrateSessionFromSource(dispatchGameState, {
          gridWidth: gridWidth || 20,
          gridHeight: gridHeight || 20,
          gridColors: gridColors || {},
          gridWalls: gridWalls || {},
          gridObjects: gridObjects || {},
          gridObjectInstances: gridObjectInstances || {},
          coins: coins || [],
          initiativeOrder: initiativeOrder || [],
          battlemapId: battlemapId || 'none'
        });
      }, 0);
    });

    // Return cleanup function
    return () => {
      console.log('CLEANUP: Removing socket event listeners');
      socket.off('connect');
      socket.off('reconnected');
      socket.off('disconnect');
      // Don't call leaveRoom() on cleanup - let the disconnect event handle it
      // This allows reconnection to work properly for page reloads
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setConnectedUsers, setPermissions, setRoomCreator, setRoomId, setInitiativeOrder, navigate,
      setUserName, setMaps, setCurrentMapId, setViewingMapId, isActivatingFromLocalRef, onRoomDeleted, leaveRoom,
      dispatchGameState, getViewingMapId, purgeLocalSessionAfterRoomEnd]);

  const attemptReconnect = useCallback(() => {
    const { roomId, userName } = getStoredRoomInfo();
    if (roomId && userName) {
      console.log('Manual reconnection attempt:', { roomId, userName });
      socket.emit('reconnect', { roomId, userName });
      return true;
    }
    return false;
  }, []);

  return {
    socket,
    createRoom,
    joinRoom,
    addUser,
    patchGameState,
    leaveRoom,
    createMap,
    duplicateMap,
    switchMap,
    renameMap,
    deleteMap,
    attemptReconnect,
    getStoredRoomInfo,
    getLastUserName,
  };
}; 